import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SharingService } from '../services/SharingService';
import LZString from 'lz-string';
import { ShareData } from '../services/FileService';
import { UrlCleaner } from '../utils/urlCleaner';

interface SharedDataContextType {
  shareData: ShareData | null;
  isLoading: boolean;
  error: string | null;
}

const SharedDataContext = createContext<SharedDataContextType | undefined>(undefined);

interface SharedDataProviderProps {
  children: ReactNode;
}

export const SharedDataProvider: React.FC<SharedDataProviderProps> = ({ children }) => {
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSharedData = () => {
      try {
        const data = SharingService.getShareDataFromUrl();
        if (!data) {
          setIsLoading(false);
          return;
        }

        const decompressedData = LZString.decompressFromEncodedURIComponent(data);
        if (!decompressedData) {
          setError('Failed to decompress share data');
          setIsLoading(false);
          return;
        }

        const parsedShareData = JSON.parse(decompressedData) as ShareData;
        setShareData(parsedShareData);
        setIsLoading(false);
        
        // Clean the URL to hide share data parameters
        UrlCleaner.cleanUrl();
      } catch (err) {
        console.error('Error loading shared data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setIsLoading(false);
      }
    };

    loadSharedData();
  }, []);

  const value: SharedDataContextType = {
    shareData,
    isLoading,
    error,
  };

  return (
    <SharedDataContext.Provider value={value}>
      {children}
    </SharedDataContext.Provider>
  );
};

export const useSharedData = (): SharedDataContextType => {
  const context = useContext(SharedDataContext);
  if (context === undefined) {
    throw new Error('useSharedData must be used within a SharedDataProvider');
  }
  return context;
};
